import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { useLanguage } from '../contexts/LanguageContext.js';
import { useNavigation } from '../contexts/NavigationContext.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faMusic, faUsers, faListCheck, faDesktop, faMobileAlt } from '@fortawesome/free-solid-svg-icons';
import './LandingPage.css';

function LandingPage() {
    const { user } = useAuth();
    const { t, language, setLanguage } = useLanguage();
    const { isMobile } = useNavigation();

    // Redirect logged-in users to dashboard
    if (user) {
        return <Navigate to="/dashboard" replace />;
    }

    const features = [
        {
            icon: faCalendarAlt,
            titleKey: 'landing.features.events.title',
            descKey: 'landing.features.events.description'
        },
        {
            icon: faListCheck,
            titleKey: 'landing.features.setlists.title',
            descKey: 'landing.features.setlists.description'
        },
        {
            icon: faUsers,
            titleKey: 'landing.features.attendance.title',
            descKey: 'landing.features.attendance.description'
        },
        {
            icon: faMusic,
            titleKey: 'landing.features.songs.title',
            descKey: 'landing.features.songs.description'
        }
    ];

    const audiences = [
        {
            image: '/images/landing/band.jpg',
            titleKey: 'landing.audiences.bands.title',
            descKey: 'landing.audiences.bands.description'
        },
        {
            image: '/images/landing/choir.jpg',
            titleKey: 'landing.audiences.choirs.title',
            descKey: 'landing.audiences.choirs.description'
        },
        {
            image: '/images/landing/sheet-music.jpg',
            titleKey: 'landing.audiences.worship.title',
            descKey: 'landing.audiences.worship.description'
        }
    ];

    return (
        <div className="landing-page">
            {/* Language Selector */}
            <div className="language-selector">
                <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="form-select form-select-sm"
                >
                    <option value="en">EN</option>
                    <option value="de">DE</option>
                    <option value="sl">SL</option>
                </select>
            </div>

            {/* Hero Section */}
            <section
                className="hero-section"
                style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/landing/hero-concert.jpg)` }}
            >
                <div className="hero-overlay"></div>
                <div className="hero-content container">
                    <div className="text-center">
                        <img
                            src="/MUSI.ico"
                            alt="MUSI Logo"
                            className="hero-logo"
                        />
                        <h1 className="hero-title">MUSI</h1>
                        <p className="hero-subtitle">{t('landing.hero.subtitle')}</p>
                        <p className="hero-description">{t('landing.hero.description')}</p>
                        <div className="hero-cta">
                            <Link to="/register" className="btn btn-primary btn-lg me-3">
                                {t('landing.hero.getStarted')}
                            </Link>
                            <Link to="/login" className="btn btn-outline-light btn-lg">
                                {t('landing.hero.login')}
                            </Link>
                        </div>
                        <div className="beta-badge mt-4">
                            <span className="badge bg-warning text-dark fs-6">
                                {t('landing.hero.betaBadge')}
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features-section py-5">
                <div className="features-container">
                    <h2 className="text-center mb-5">{t('landing.features.title')}</h2>
                    <div className="features-grid">
                        {features.map((feature, index) => (
                            <div key={index} className="features-grid-item">
                                <div className="feature-card text-center p-4">
                                    <div className="feature-icon mb-3">
                                        <FontAwesomeIcon icon={feature.icon} size="3x" />
                                    </div>
                                    <h4>{t(feature.titleKey)}</h4>
                                    <p className="text-muted">{t(feature.descKey)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Who Is It For Section */}
            <section className="audiences-section py-5 bg-light">
                <div className="audiences-container">
                    <h2 className="text-center mb-5">{t('landing.audiences.title')}</h2>
                    <div className="audiences-grid">
                        {audiences.map((audience, index) => (
                            <div key={index} className="audiences-grid-item">
                                <div className="audience-card">
                                    <div className="audience-image-wrapper">
                                        <img
                                            src={audience.image}
                                            alt={t(audience.titleKey)}
                                            className="audience-image"
                                        />
                                    </div>
                                    <div className="audience-content p-4">
                                        <h4>{t(audience.titleKey)}</h4>
                                        <p className="text-muted mb-0">{t(audience.descKey)}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Screenshots Section - Desktop & Mobile */}
            <section className="screenshots-section py-5">
                <div className="container">
                    <h2 className="text-center mb-3">{t('landing.screenshots.title')}</h2>
                    <p className="text-center text-muted mb-5">{t('landing.screenshots.subtitle')}</p>
                    <div className="row align-items-center justify-content-center g-4">
                        <div className="col-lg-7 col-md-8">
                            <div className="screenshot-desktop">
                                <div className="screenshot-label">
                                    <FontAwesomeIcon icon={faDesktop} className="me-2" />
                                    {t('landing.screenshots.desktop')}
                                </div>
                                <img
                                    src="/images/landing/screenshot-desktop.png"
                                    alt="MUSI Desktop View"
                                    className="img-fluid rounded shadow"
                                />
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-4 col-6">
                            <div className="screenshot-mobile">
                                <div className="screenshot-label">
                                    <FontAwesomeIcon icon={faMobileAlt} className="me-2" />
                                    {t('landing.screenshots.mobile')}
                                </div>
                                <img
                                    src="/images/landing/screenshot-mobile.jpg"
                                    alt="MUSI Mobile View"
                                    className="img-fluid rounded shadow"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-md-6 text-center text-md-start">
                            <span className="footer-text">
                                © {new Date().getFullYear()} MUSI
                            </span>
                        </div>
                        <div className="col-md-6 text-center text-md-end mt-3 mt-md-0">
                            <Link to="/terms" className="footer-link me-3">
                                {t('landing.footer.terms')}
                            </Link>
                            <Link to="/privacy" className="footer-link">
                                {t('landing.footer.privacy')}
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default LandingPage;
